// =============================================================================
// features/meu-whatsapp/useSessionStatusRealtime.ts — Push em tempo real do
// estado da sessão WAHA do vendedor (F43-S12).
//
// Assina `channel.session_status` no socket já conectado pelo SocketProvider
// global (sala `user:{id}`, join automático no handshake — mesma sala que
// `notification.new` já usa). O backend publica este evento a cada
// `session.status` que o WAHA entrega (waha-webhook/service.ts).
//
// Não faz update otimista do payload: apenas invalida `MEU_WHATSAPP_KEY`, que
// cobre tanto `useMeuPareamento` (status/telefone) quanto `useMeuQr`. A fonte
// de verdade continua sendo `GET /api/channels/waha/me` — o socket só encurta
// o tempo até o refetch acontecer, para o vendedor não precisar recarregar a
// página (DoD do slot) nem esperar o próximo poll de `useMeuPareamento`.
//
// Extraído em função pura (sem React), no mesmo desenho de
// `useQuickRepliesRealtime.ts`: o projeto não tem @testing-library/react
// instalado, e o DoD exige testar registro/remoção do listener.
// =============================================================================
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { useSocket } from '../../lib/realtime/useSocket';

import { MEU_WHATSAPP_KEY } from './useMeuWhatsApp';

/** Nome do evento de socket (waha-webhook/service.ts, F43-S12). */
export const CHANNEL_SESSION_STATUS_EVENT = 'channel.session_status';

/** Payload publicado pelo backend — espelha `RecordWahaSessionStatusResult` sem PII. */
export interface ChannelSessionStatusPayload {
  readonly channelId: string;
  readonly status: string;
  readonly previousStatus: string | null;
  readonly statusAt: string;
}

/**
 * Recorte mínimo do `Socket` do socket.io-client, no mesmo desenho de
 * `QuickRepliesRealtimeSocket` — evita as sobrecargas genéricas de eventos
 * reservados (`connect`/`disconnect`) que tornam um mock simples incompatível
 * em compilação.
 */
export interface SessionStatusRealtimeSocket {
  on(event: string, listener: (payload: ChannelSessionStatusPayload) => void): unknown;
  off(event: string, listener: (payload: ChannelSessionStatusPayload) => void): unknown;
}

/** Recorte mínimo de QueryClient usado aqui — facilita o mock em teste. */
export type SessionStatusRealtimeQueryClient = Pick<QueryClient, 'invalidateQueries'>;

/**
 * Registra o listener de `channel.session_status` no socket fornecido e
 * retorna a função de cleanup. Função pura — testável isoladamente.
 */
export function attachSessionStatusRealtimeListener(
  socket: SessionStatusRealtimeSocket,
  queryClient: SessionStatusRealtimeQueryClient,
): () => void {
  function handleSessionStatus(_payload: ChannelSessionStatusPayload): void {
    // Prefix match: invalida tanto ['meu-whatsapp','me'] quanto
    // ['meu-whatsapp','qr'] — o QR também deixa de fazer sentido quando a
    // sessão muda de estado (ex.: WORKING → o QR antigo não serve mais).
    void queryClient.invalidateQueries({ queryKey: MEU_WHATSAPP_KEY });
  }

  socket.on(CHANNEL_SESSION_STATUS_EVENT, handleSessionStatus);

  return () => {
    socket.off(CHANNEL_SESSION_STATUS_EVENT, handleSessionStatus);
  };
}

/**
 * useSessionStatusRealtime — assina `channel.session_status` e invalida o
 * cache do meu pareamento. Seguro para múltiplas montagens simultâneas
 * (`MeuWhatsAppPage` e `MeuWhatsAppInboxPage` podem montar em navegações
 * distintas) — cada instância registra e remove o próprio handler.
 */
export function useSessionStatusRealtime(): void {
  const socket = useSocket();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!socket) return;
    return attachSessionStatusRealtimeListener(socket, queryClient);
  }, [socket, queryClient]);
}
